import { STRICT_CONSENT_REGIONS, type Consent } from "./consent";
import { googleEvent } from "./moments";
import type { Sink } from "./sink";

/**
 * Google Analytics, loaded by our own code (the CSP allows no inline script),
 * with Google's consent mode set before anything is measured.
 *
 * Google applies the regional default itself, from the visitor's IP: the EU,
 * UK and Switzerland start declined, everyone else allowed. Only the three
 * steps Google Ads bids on are sent; no user id, no email, no birth details.
 */
declare global {
  interface Window {
    /** Google's tag queue; gtag.js drains it once it loads. */
    dataLayer?: unknown[];
  }
}

/** How long the tag waits for a stored choice before measuring. */
const WAIT_FOR_UPDATE_MS = 500;

const TAG_URL = "https://www.googletagmanager.com/gtag/js?id=";

function consentState(consent: Consent): Record<string, Consent> {
  return { ad_storage: consent, ad_user_data: consent, ad_personalization: consent, analytics_storage: consent };
}

interface Page {
  readonly measurementId: string;
  readonly win: { dataLayer?: unknown[] };
  readonly doc: Document;
  /** The visitor's stored choice, if they have made one. */
  readonly consent: Consent | null;
}

export function createGoogleSink({ measurementId, win, doc, consent }: Page): Sink {
  const dataLayer = (win.dataLayer ??= []);
  // gtag.js reads each entry as an `arguments` object, not an array.
  function gtag(..._args: unknown[]): void {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  }

  if (consent) {
    gtag("consent", "default", consentState(consent));
  } else {
    gtag("consent", "default", { ...consentState("denied"), region: STRICT_CONSENT_REGIONS, wait_for_update: WAIT_FOR_UPDATE_MS });
    gtag("consent", "default", { ...consentState("granted"), wait_for_update: WAIT_FOR_UPDATE_MS });
  }
  gtag("js", new Date());
  gtag("config", measurementId);

  const script = doc.createElement("script");
  script.async = true;
  script.src = `${TAG_URL}${encodeURIComponent(measurementId)}`;
  doc.head.appendChild(script);

  return {
    track(moment) {
      const event = googleEvent(moment);
      if (event) gtag("event", event[0], event[1]);
    },
    setConsent(choice) {
      gtag("consent", "update", consentState(choice));
    },
    // Google gets no identity and follows page changes on its own.
    identify: () => undefined,
    forget: () => undefined,
    screen: () => undefined,
  };
}
